import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, CalendarClockIcon, ClipboardCheckIcon, LockIcon, MapPinIcon, SearchIcon, ShieldIcon, ShoppingBagIcon, WalletIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { HeroDashboard } from '../../components/landing/HeroDashboard';
import { FloatingStatusCard } from '../../components/landing/FloatingStatusCard';
import { TrustStrip } from '../../components/landing/TrustStrip';
import { EcosystemMap } from '../../components/landing/EcosystemMap';
import { ProductShowcase } from '../../components/landing/ProductShowcase';
import { IdentityBanner } from '../../components/landing/IdentityBanner';
import { PersonalizationSection } from '../../components/landing/PersonalizationSection';
import { CampusCarousel } from '../../components/landing/CampusCarousel';
import { FinalCTA } from '../../components/landing/FinalCTA';
import { landingImages } from '../../data/landingVisual';
import { publicCapabilities } from '../../data/auth';
import { useNetwork } from '../../contexts/NetworkContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * The public front door.
 *
 * Every claim here is either derived from real records or absent — no invented
 * totals, no fabricated partnerships, and no campus shown as operating unless
 * its register entry says so. Demonstration content used to show what a screen
 * looks like is labelled as such.
 */
const ENTER = {
  duration: 0.32,
  ease: [0.23, 1, 0.32, 1] as const
};
export function Landing() {
  /* useNetwork returns the value object itself, not a { network } wrapper. */
  const network = useNetwork();
  const {
    authenticated,
    identity
  } = useAuth();
  const published = network.campuses.filter(campus => campus.published);
  return <div className="bg-surface" data-id="element-9624" data-mp="1dgksipgd54ov">
      {/* -------------------------------------------------------------- hero */}
      <section className="relative overflow-hidden" data-id="element-9625" data-mp="1kk2jhd09dm4t">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-8 lg:py-16" data-id="element-9626" data-mp="1phkrn16gbxbr">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={ENTER} className="relative z-10" data-id="element-9627" data-mp="1ufmabkzytump">
            <p className="inline-flex items-center rounded-full border border-brand-100 bg-brand-50 px-3 py-1 text-caption font-semibold uppercase tracking-wide text-brand-800" data-id="element-9628" data-mp="238pj3fzo1jb0">
              The campus operating system
            </p>
            <h1 className="mt-5 text-display font-bold leading-[1.08] tracking-tight text-ink" data-id="element-9629" data-mp="2d5kdpnstizk0">
              Your campus.
              <br data-id="element-9630" data-mp="itxb3f3oput2" />
              Your journey.
              <br data-id="element-9631" data-mp="iv8gfjcs3ama" />
              <span className="text-brand-700" data-id="element-9632" data-mp="ivqmt8t5n57x">One account.</span>
            </h1>
            <p className="mt-5 max-w-[46ch] text-body-lg leading-7 text-ink-muted" data-id="element-9633" data-mp="nsqrbfacvzwx">
              Campuzon brings academics, campus services, housing, marketplace, opportunities and
              student life together in one trusted platform.
            </p>

            <div className="mt-8 flex flex-wrap gap-3" data-id="element-9634" data-mp="2s9n60oeaxsvk">
              {authenticated ? <Link to="/" data-id="element-9635" data-mp="2nc4x2y0abdnk">
                  <Button variant="primary" icon={<ArrowRightIcon className="h-5 w-5" data-id="element-9637" data-mp="2n9irv50v9a8i" />} data-id="element-9636" data-mp="2ncwtuc5mbhff">
                    Continue as @{identity?.username}
                  </Button>
                </Link> : <Link to="/register" data-id="element-9638" data-mp="3g0srbj30hdd9">
                  <Button variant="primary" icon={<ArrowRightIcon className="h-5 w-5" data-id="element-9640" data-mp="3azmtx7yep1xe" />} data-id="element-9639" data-mp="3b0o8vnijm8ke">
                    Create your account
                  </Button>
                </Link>}
              <Link to="/welcome/campuses" data-id="element-9641" data-mp="1zlhp6cqdco10">
                <Button variant="secondary" icon={<SearchIcon className="h-5 w-5" data-id="element-9643" data-mp="1zjep0zvi5eik" />} data-id="element-9642" data-mp="1znca3r1n4g53">
                  Explore Campuzon
                </Button>
              </Link>
            </div>

            <p className="mt-8 max-w-[40ch] text-body-sm text-ink-muted" data-id="element-9644" data-mp="3iib76djw3o60">
              Built for students, campuses and trusted providers — one account, and only the
              consoles your roles actually hold.
            </p>
          </motion.div>

          {/* Layered composition: campus behind, product preview, student in front. */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...ENTER,
          delay: 0.08
        }} className="relative" data-id="element-9645" data-mp="3efeluf2ytdyd">
            {/* The campus sits behind as a soft field rather than a photograph
                with an edge, so the student cut-out lands on the same near-white
                ground her own frame carries and no rectangle is visible. */}
            <div className="relative overflow-hidden rounded-panel bg-surface" style={{
            aspectRatio: '16 / 11'
          }} data-id="element-9646" data-mp="2gtqxmhgny1xx">
              <img src={landingImages.heroCampus} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" data-id="element-9647" data-mp="2lqqx29j0c6g2" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-l from-white via-white/75 to-white/25" data-id="element-9648" data-mp="29clrd3y8ey83" />

              <div className="absolute inset-y-0 left-0 flex w-[72%] items-center pl-[3%]" data-id="element-9649" data-mp="2ic8sdon1u20m">
                <HeroDashboard data-id="element-9650" data-mp="1ummy9fljaczh" />
              </div>

              <img src={landingImages.heroStudent} alt="A university student using Campuzon on her phone" className="absolute bottom-0 right-[-6%] h-[94%] w-auto object-contain object-bottom" data-id="element-9651" data-mp="1uj8rlv5u7hm8" />
            </div>

            {/* Real interface fragments, floated over the composition. */}
            <FloatingStatusCard icon={BadgeCheckIcon} label="Verified student" tone="brand" className="left-[-2%] top-[6%] hidden sm:flex" delay={0.24} data-id="element-9652" data-mp="1bu29a98ygs0y" />
            <FloatingStatusCard icon={CalendarClockIcon} value="2" label="Classes today" tone="info" className="left-[-4%] top-[26%] hidden sm:flex" delay={0.32} data-id="element-9653" data-mp="36w5wov4h9y8o" />
            <FloatingStatusCard icon={ClipboardCheckIcon} value="4" label="Tasks due" tone="info" className="right-[-3%] top-[10%] hidden lg:flex" delay={0.4} data-id="element-9654" data-mp="3lm4ugws4f7nm" />
            <FloatingStatusCard icon={WalletIcon} value="₦450k" label="Housing budget" tone="brand" className="right-[-5%] top-[38%] hidden lg:flex" delay={0.48} data-id="element-9655" data-mp="2xxlkmctfamtf" />
            <FloatingStatusCard icon={ShoppingBagIcon} value="3" label="Orders" tone="violet" className="right-[-2%] bottom-[16%] hidden lg:flex" delay={0.56} data-id="element-9656" data-mp="d6tg1v74v4me" />
            <FloatingStatusCard icon={MapPinIcon} label="Engineering Week" meta="Mon 17 Aug" tone="warn" className="bottom-[4%] left-[6%] hidden sm:flex" delay={0.64} data-id="element-9657" data-mp="1xovn3y4bxtdy" />
          </motion.div>
        </div>
      </section>

      <TrustStrip campuses={network.campuses} data-id="element-9658" data-mp="1j0rb1czs8g45" />

      <EcosystemMap data-id="element-9659" data-mp="1iyeosnmzrjla" />

      <ProductShowcase data-id="element-9660" data-mp="1e1o7jwzgahya" />

      <IdentityBanner data-id="element-9661" data-mp="1e0d3rx609a76" />

      <PersonalizationSection data-id="element-9662" data-mp="1dzv0whou7xqa" />

      <CampusCarousel campuses={published} data-id="element-9663" data-mp="19zt7evt9aam3" />

      {/* --------------------------------------------------------- trust */}
      <section aria-labelledby="trust-heading" className="border-t border-line bg-surface py-16" data-id="element-9664" data-mp="1519iqnna2kq1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-9665" data-mp="34edm8ruwbbxk">
          <div className="max-w-[58ch]" data-id="element-9666" data-mp="34brfuvjqr2gx">
            <h2 id="trust-heading" className="text-h2 font-bold tracking-tight text-ink" data-id="element-9667" data-mp="2ze7ulbomlpfh">
              What a badge on Campuzon actually means
            </h2>
            <p className="mt-3 text-body-lg text-ink-muted" data-id="element-9668" data-mp="28bhtx0ywkpxh">
              Verification is a statement about a specific check, never a general reassurance.
            </p>
          </div>

          <div className="mt-9 grid gap-4 lg:grid-cols-3" data-id="element-9669" data-mp="1yjbwvctsnibf">
            {[{
            title: 'Campuzon Verified',
            icon: ShieldIcon,
            body: 'An authorised representative inspected the listing in person and recorded evidence. It is not a guarantee about the landlord or the transaction.'
          }, {
            title: 'Not inspected by Campuzon',
            icon: SearchIcon,
            body: 'Listed by its owner and not checked. It is shown to you with that stated, rather than hidden or quietly mixed in with verified listings.'
          }, {
            title: 'What we never do',
            icon: LockIcon,
            body: 'Campuzon never asks for your university password, never sells your records, and never shows your notes, diary, results or balances to anyone — including your university.'
          }].map(item => {
            const Icon = item.icon;
            return <article key={item.title} className="rounded-card border border-line bg-canvas px-5 py-5" data-id="element-9670" data-mp="189uye5efatp6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50" data-id="element-9671" data-mp="1lz55b70a2pkv">
                    <Icon aria-hidden className="h-5 w-5 text-brand-700" data-id="element-9672" data-mp="1h3zje86vlaws" />
                  </span>
                  <h3 className="mt-3.5 text-body font-semibold text-ink" data-id="element-9673" data-mp="1vx32wl411yq5">{item.title}</h3>
                  <p className="mt-1.5 max-w-[46ch] text-body-sm text-ink-muted" data-id="element-9674" data-mp="1r0ufyljl918f">{item.body}</p>
                </article>;
          })}
          </div>

          <div className="mt-12 max-w-[58ch]" data-id="element-9675" data-mp="3ss7gyzifqoz7">
            <h3 className="text-h3 font-semibold tracking-tight text-ink" data-id="element-9676" data-mp="3stiq7eungoxp">
              What you can do before signing in
            </h3>
            <p className="mt-2 text-body text-ink-muted" data-id="element-9677" data-mp="yuwxxxg0f4u4">
              Browsing is open. An account is required only where an action belongs to you
              personally.
            </p>
          </div>

          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-9678" data-mp="oy261xe8s2ot">
            {publicCapabilities.map(capability => <li key={capability.title} className="rounded-card border border-line bg-canvas px-5 py-5" data-id="element-9679" data-mp="1mkhn4y2raybu">
                <div className="flex items-start justify-between gap-3" data-id="element-9680" data-mp="1cupn3obw1czz">
                  <p className="text-body font-semibold text-ink" data-id="element-9681" data-mp="1csv2kb4j3mwf">{capability.title}</p>
                  <Badge tone={capability.open ? 'ok' : 'warn'} data-id="element-9682" data-mp="17u1y1877z68e">
                    {capability.open ? 'Open to visitors' : 'Account needed'}
                  </Badge>
                </div>
                <p className="mt-2 max-w-[44ch] text-body-sm text-ink" data-id="element-9683" data-mp="20jha81amf55w">{capability.description}</p>
                <p className="mt-2 max-w-[44ch] text-caption text-ink-subtle" data-id="element-9684" data-mp="28v95w69m9tba">{capability.gate}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <FinalCTA authenticated={authenticated} username={identity?.username} data-id="element-9685" data-mp="2htctr982r269" />
    </div>;
}